import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  casinoCategoriesQuery,
  casinoFilterContext,
  useAuthSession,
} from '@/api';
import { BannerPosition } from '@/domain';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { BannerHeroCarousel, useBanners } from '@/ui/cms';
import { CasinoCategoryNav } from './CasinoCategoryNav';
import { CasinoHeaderRow } from './CasinoHeaderRow';

export interface CasinoChromeProps {
  activeNavId: string;
  showHero?: boolean;
  showCategoryNav?: boolean;
  providersActive?: boolean;
  searchValue?: string;
  onSearchCommit?: (value: string) => void;
  children: ReactNode;
}

export function CasinoChrome({
  activeNavId,
  showHero = false,
  showCategoryNav = true,
  providersActive = false,
  searchValue,
  onSearchCommit,
  children,
}: CasinoChromeProps) {
  const isDesktop = useIsDesktop();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const ctx = casinoFilterContext(account, user);
  const categoriesQuery = useQuery(casinoCategoriesQuery(ctx));
  const heroBanners = useBanners(
    isDesktop ? BannerPosition.Casino : BannerPosition.CasinoMobile,
  );

  const showFavorites = Boolean(user?.Guid) && (user?.FavoriteGamesCount ?? 0) > 0;

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);

  return (
    <div className="flex w-full flex-col gap-4">
      {showHero ? (
        <BannerHeroCarousel
          banners={heroBanners}
          size={isDesktop ? 'desktop' : 'mobile'}
          className="w-full"
        />
      ) : null}
      <CasinoHeaderRow
        key={searchValue ?? ''}
        searchValue={searchValue}
        onSearchCommit={onSearchCommit}
        providersActive={providersActive}
      />
      {showCategoryNav ? (
        <CasinoCategoryNav
          activeId={activeNavId}
          categories={categories}
          showFavorites={showFavorites || Boolean(user?.Guid)}
        />
      ) : null}
      {children}
    </div>
  );
}
